import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Image, Platform, Pressable, ScrollView, StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Clipboard from 'expo-clipboard';
import { apiService } from '../../services/api';
import { colors, spacing, typography, withAlpha } from '../../theme';
import { useLocale } from '../../contexts/LocaleContext';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useIsMobileBrowser } from '../../hooks/useIsMobileBrowser';
import { useAppStore } from '../../store/appStore';
import { useGlobalSearchStore } from '../../store/globalSearchStore';
import { InsetModal } from '../InsetModal';
import { overlayBackdropStyle, overlayCardStyle } from '../OverlayPanel';
import { searchNavigation } from './searchNavigation';
import type { SearchGroup, SearchGroupResult, SearchHit, SearchResponse, SearchTarget, SearchTone } from './searchTypes';

const isWeb = Platform.OS === 'web';
const DEBOUNCE_MS = 160;
const MAX_RECENT = 8;
const NO_GROUPS: SearchGroupResult[] = [];

const GROUP_ICONS: Record<SearchGroup, string> = {
  actions: 'flash-outline', pages: 'compass-outline', contacts: 'people-outline', orders: 'receipt-outline', quotations: 'document-text-outline',
  products: 'cube-outline', vendors: 'business-outline', catalogues: 'albums-outline', data_lists: 'list-outline', templates: 'chatbox-ellipses-outline',
  documents: 'documents-outline', flows: 'git-network-outline', events: 'calendar-outline', team: 'person-outline', departments: 'git-merge-outline',
};
/** Typing one of these narrows the search ("order: 1042"). */
const PREFIX_OF: Partial<Record<SearchGroup, string>> = { contacts: 'contact', orders: 'order', quotations: 'quote', products: 'product', vendors: 'vendor', pages: 'page' };

type RecentHit = Pick<SearchHit, 'id' | 'group' | 'title' | 'subtitle' | 'icon' | 'image' | 'target'>;
type Recents = { queries: string[]; opened: RecentHit[] };
/** Selectable rows, in display order, for the keyboard. */
type Row = { kind: 'hit'; hit: SearchHit } | { kind: 'recent'; hit: RecentHit } | { kind: 'query'; text: string } | { kind: 'seeAll'; group: SearchGroupResult } | { kind: 'ask' };

const recentKey = (businessId: string) => `globalSearch:v1:${businessId}`;
async function readRecents(businessId: string): Promise<Recents> {
  try { const raw = await AsyncStorage.getItem(recentKey(businessId)); const v = raw ? JSON.parse(raw) : null; return { queries: Array.isArray(v?.queries) ? v.queries : [], opened: Array.isArray(v?.opened) ? v.opened : [] }; } catch { return { queries: [], opened: [] }; }
}

// Arabic letter forms match each other when highlighting, as in the server search.
const FORMS = ['اأإآٱ', 'هة', 'يى', 'وؤ'];
const pattern = (word: string) => [...word].map((ch) => { const g = FORMS.find((f) => f.includes(ch)); return g ? `[${g}]` : ch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }).join('');
function highlightParts(text: string, query: string): Array<{ text: string; match: boolean }> {
  const words = query.replace(/^[^:：]{1,20}[:：]\s*/, '').trim().split(/\s+/).filter((w) => w.length > 0).slice(0, 6);
  if (!words.length || !text) return [{ text, match: false }];
  try {
    // split() with one capture group: odd parts are the matches.
    const re = new RegExp(`(${words.map(pattern).join('|')})`, 'i');
    return text.split(new RegExp(re.source, 'gi')).map((part, i) => ({ text: part, match: i % 2 === 1 })).filter((p) => p.text);
  } catch { return [{ text, match: false }]; }
}

interface Props {
  visible: boolean;
  onClose: () => void;
  /** Navigates to a screen (already validated). */
  onNavigate: (nav: NonNullable<ReturnType<typeof searchNavigation>>) => void;
  /** Present when the user may use the workspace assistant. */
  onAsk?: (question: string) => void;
}

/**
 * The app-wide search: pages, quick actions and every record the user may see, grouped by kind,
 * with recent searches and recently opened records when empty. Keyboard first on desktop
 * (↑ ↓ to move, ↵ to open, Esc to close), a full-screen sheet on phones.
 */
export const GlobalSearch: React.FC<Props> = ({ visible, onClose, onNavigate, onAsk }) => {
  const styles = useThemedStyles(getStyles);
  const { t, locale } = useLocale();
  const rtl = useRTLStyles();
  const isMobileBrowser = useIsMobileBrowser();
  const desktop = isWeb && !isMobileBrowser;
  const tr = (key: string, values?: Record<string, any>) => t(`search.${key}` as any, values);
  const businessId = useAppStore((s) => String(s.currentBusiness?._id || (s.currentBusiness as any)?.id || ''));
  const initial = useGlobalSearchStore((s) => s.initial);

  const [query, setQuery] = useState('');
  const [group, setGroup] = useState<SearchGroup | null>(null);
  const [result, setResult] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState(0);
  const [copied, setCopied] = useState<string | null>(null);
  const [recents, setRecents] = useState<Recents>({ queries: [], opened: [] });
  const [retry, setRetry] = useState(0);
  const input = useRef<TextInput>(null);
  const abort = useRef<AbortController | null>(null);
  const cache = useRef(new Map<string, SearchResponse>());
  const scroll = useRef<ScrollView>(null);

  // Fresh on every open; results from the last few searches stay cached for instant back-typing.
  useEffect(() => {
    if (!visible) return;
    setQuery(initial); setGroup(null); setResult(null); setError(false); setSelected(0);
    readRecents(businessId).then(setRecents);
    const focus = setTimeout(() => input.current?.focus(), 60);
    return () => clearTimeout(focus);
  }, [visible, businessId, initial]);
  useEffect(() => { cache.current.clear(); }, [businessId]);

  const trimmed = query.trim();
  useEffect(() => {
    if (!visible) return;
    abort.current?.abort();
    if (!trimmed) { setResult(null); setLoading(false); setError(false); return; }
    const key = `${group || ''}|${trimmed.toLowerCase()}`;
    const hit = cache.current.get(key);
    if (hit) { setResult(hit); setLoading(false); setError(false); setSelected(0); return; }
    setLoading(true);
    const controller = new AbortController();
    abort.current = controller;
    const timer = setTimeout(async () => {
      try {
        const res = await apiService.globalSearch(trimmed, { group: group || undefined, locale, signal: controller.signal });
        if (controller.signal.aborted) return;
        if (res.statusCode !== 1000 || !res.data) throw new Error('search failed');
        if (cache.current.size > 40) cache.current.clear();
        cache.current.set(key, res.data);
        setResult(res.data); setError(false); setSelected(0);
      } catch {
        if (!controller.signal.aborted) setError(true);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [trimmed, group, visible, locale, retry]);

  // Stable when there are no results — a fresh [] each render would loop the chip effect below.
  const groups = useMemo(() => result?.groups || NO_GROUPS, [result]);
  const rows: Row[] = useMemo(() => {
    if (!trimmed) return [...(onAsk ? [{ kind: 'ask' as const }] : []), ...recents.opened.map((hit) => ({ kind: 'recent' as const, hit })), ...recents.queries.map((text) => ({ kind: 'query' as const, text }))];
    const out: Row[] = [];
    for (const g of groups) {
      for (const hit of g.hits) out.push({ kind: 'hit', hit });
      if (g.hasMore && g.seeAll && !group) out.push({ kind: 'seeAll', group: g });
    }
    if (onAsk) out.push({ kind: 'ask' });
    return out;
  }, [trimmed, groups, recents, group, onAsk]);

  const remember = useCallback(async (hit?: RecentHit) => {
    const next: Recents = {
      queries: trimmed ? [trimmed, ...recents.queries.filter((q) => q.toLowerCase() !== trimmed.toLowerCase())].slice(0, MAX_RECENT) : recents.queries,
      opened: hit ? [{ id: hit.id, group: hit.group, title: hit.title, subtitle: hit.subtitle, icon: hit.icon, image: hit.image, target: hit.target }, ...recents.opened.filter((o) => o.id !== hit.id)].slice(0, MAX_RECENT) : recents.opened,
    };
    setRecents(next);
    try { await AsyncStorage.setItem(recentKey(businessId), JSON.stringify(next)); } catch { /* best effort */ }
  }, [trimmed, recents, businessId]);

  const go = useCallback((target: SearchTarget, hit?: RecentHit) => {
    const nav = searchNavigation(target);
    if (!nav) return;
    void remember(hit);
    onClose();
    onNavigate(nav);
  }, [remember, onClose, onNavigate]);

  const ask = useCallback(() => {
    if (!onAsk) return;
    if (trimmed) void remember();
    onClose();
    onAsk(trimmed);
  }, [onAsk, trimmed, remember, onClose]);

  const activate = useCallback((row?: Row) => {
    if (!row) return;
    if (row.kind === 'hit' || row.kind === 'recent') go(row.hit.target, row.hit);
    else if (row.kind === 'query') setQuery(row.text);
    else if (row.kind === 'seeAll') { if (row.group.seeAll) go(row.group.seeAll); }
    else if (row.kind === 'ask') ask();
  }, [go, ask]);

  const copy = async (value: string, id: string) => {
    try {
      if (isWeb && navigator.clipboard) await navigator.clipboard.writeText(value); else await Clipboard.setStringAsync(value);
      setCopied(id); setTimeout(() => setCopied((c) => (c === id ? null : c)), 1400);
    } catch { /* clipboard blocked */ }
  };

  // Keyboard: ↑ ↓ move, ↵ open, ⌘/Ctrl+↵ the first other action (edit), Esc close, Tab next filter.
  useEffect(() => {
    if (!visible || !isWeb) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setSelected((i) => (rows.length ? (i + (e.key === 'ArrowDown' ? 1 : -1) + rows.length) % rows.length : 0));
      } else if (e.key === 'Enter' && !e.isComposing) {
        e.preventDefault();
        const row = rows[selected];
        if ((e.metaKey || e.ctrlKey) && row?.kind === 'hit' && row.hit.secondary?.[0]) go(row.hit.secondary[0].target, row.hit);
        else activate(row);
      } else if (e.key === 'Escape') {
        e.preventDefault(); onClose();
      } else if (e.key === 'Tab' && trimmed && groups.length) {
        e.preventDefault();
        const keys: Array<SearchGroup | null> = [null, ...groups.map((g) => g.key)];
        const at = keys.indexOf(group);
        setGroup(keys[(at + (e.shiftKey ? -1 : 1) + keys.length) % keys.length]);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [visible, rows, selected, activate, go, onClose, trimmed, groups, group]);

  // Keep the selected row in view.
  useEffect(() => {
    if (!isWeb || !visible) return;
    const el = typeof document !== 'undefined' ? document.getElementById(`gs-row-${selected}`) : null;
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [selected, visible]);

  // Filter chips: the groups found (or the one chosen), so the counts are honest.
  const [chipGroups, setChipGroups] = useState<SearchGroupResult[]>([]);
  useEffect(() => { if (!group) setChipGroups((prev) => (prev === groups ? prev : groups)); }, [groups, group]);
  useEffect(() => { if (!trimmed) setGroup(null); }, [trimmed]);

  const money = (value: number, currency?: string) => {
    try { return new Intl.NumberFormat(locale, currency ? { style: 'currency', currency, maximumFractionDigits: 2 } : { maximumFractionDigits: 2 }).format(value); } catch { return `${value} ${currency || ''}`.trim(); }
  };
  const when = (iso?: string) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const days = Math.round((Date.now() - d.getTime()) / 86_400_000);
    if (days === 0) return tr('today');
    if (days === 1) return tr('yesterday');
    if (days > 1 && days < 7) return tr('daysAgo', { count: days });
    try { return d.toLocaleDateString(locale, { day: 'numeric', month: 'short', ...(d.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}) }); } catch { return d.toISOString().slice(0, 10); }
  };
  const toneColor = (tone: SearchTone) => (({ success: colors.success, warning: colors.warning, danger: colors.error, info: colors.info, neutral: colors.text.secondary } as any)[tone] as string);

  const Highlight = ({ text, style }: { text: string; style: any }) => (
    <Text style={[style, { textAlign: rtl.textStart }]} numberOfLines={1}>
      {highlightParts(text, trimmed).map((p, i) => <Text key={i} style={p.match ? styles.mark : undefined}>{p.text}</Text>)}
    </Text>
  );

  let index = -1;
  const rowProps = (i: number) => ({
    nativeID: `gs-row-${i}`,
    onHoverIn: desktop ? () => setSelected(i) : undefined,
    accessibilityRole: 'button' as const,
    style: ({ pressed }: any) => [styles.row, { flexDirection: rtl.rowDirection }, (i === selected && desktop) || pressed ? styles.rowActive : null],
  });

  const renderHit = (hit: SearchHit | RecentHit, recent = false) => {
    const i = ++index;
    const full = hit as SearchHit;
    const active = i === selected;
    return (
      <Pressable key={`${recent ? 'r' : 'h'}-${hit.id}`} {...rowProps(i)} onPress={() => go(hit.target, hit)}>
        {hit.image ? <Image source={{ uri: hit.image }} style={styles.thumb} /> : (
          <View style={[styles.iconBox, hit.group === 'actions' && styles.iconBoxAction]}>
            <Ionicons name={(hit.icon || GROUP_ICONS[hit.group]) as any} size={18} color={(hit.group === 'actions' ? colors.primary : colors.text.secondary) as string} />
          </View>
        )}
        <View style={styles.rowText}>
          <View style={[styles.titleLine, { flexDirection: rtl.rowDirection }]}>
            <Highlight text={hit.title} style={styles.rowTitle} />
            {!!full.badge && (
              <View style={[styles.badge, { backgroundColor: withAlpha(toneColor(full.badge.tone), 0.14) }]}>
                <Text style={[styles.badgeText, { color: toneColor(full.badge.tone) }]} numberOfLines={1}>{full.badge.label}</Text>
              </View>
            )}
          </View>
          {!!(hit.subtitle || recent) && <Highlight text={[hit.subtitle, recent ? tr(`group.${hit.group}`) : ''].filter(Boolean).join(' · ')} style={styles.rowSubtitle} />}
          {!!full.meta?.length && (
            <View style={[styles.metaLine, { flexDirection: rtl.rowDirection }]}>
              {full.meta.slice(0, 3).map((m) => <Text key={m} style={styles.metaChip} numberOfLines={1}>{m}</Text>)}
            </View>
          )}
        </View>
        <View style={[styles.rowEnd, { alignItems: rtl.isRTL ? 'flex-start' : 'flex-end' }]}>
          {!!full.amount && <Text style={styles.amount}>{money(full.amount.value, full.amount.currency)}</Text>}
          {!!full.date && <Text style={styles.date}>{when(full.date)}</Text>}
        </View>
        {(active || !desktop) && (!!full.secondary?.length || !!full.copy) && (
          <View style={[styles.rowActions, { flexDirection: rtl.rowDirection }]}>
            {!!full.copy && (
              <TouchableOpacity accessibilityLabel={tr('copy')} style={styles.miniButton} onPress={() => copy(full.copy!, hit.id)}>
                <Ionicons name={copied === hit.id ? 'checkmark' : 'copy-outline'} size={15} color={(copied === hit.id ? colors.success : colors.text.secondary) as string} />
              </TouchableOpacity>
            )}
            {full.secondary?.map((s) => (
              <TouchableOpacity key={s.key} accessibilityLabel={s.label} style={styles.miniButton} onPress={() => go(s.target, hit)}>
                <Ionicons name={s.icon as any} size={15} color={colors.text.secondary as string} />
                {desktop && <Text style={styles.miniText}>{s.label}</Text>}
              </TouchableOpacity>
            ))}
          </View>
        )}
        {active && desktop && <Ionicons name={rtl.isRTL ? 'return-down-back' : 'return-down-forward'} size={15} color={colors.text.disabled as string} />}
      </Pressable>
    );
  };

  const sectionHeader = (key: string, label: string, icon: string, right?: React.ReactNode) => (
    <View key={`s-${key}`} style={[styles.sectionHeader, { flexDirection: rtl.rowDirection }]}>
      <Ionicons name={icon as any} size={13} color={colors.text.secondary as string} />
      <Text style={styles.sectionTitle}>{label}</Text>
      <View style={styles.flex} />
      {right}
    </View>
  );

  const body = () => {
    index = -1;
    if (!trimmed) {
      const hasRecents = recents.opened.length || recents.queries.length;
      return (
        <>
          {!!onAsk && (() => {
            const i = ++index;
            return (
              <Pressable {...rowProps(i)} onPress={ask}>
                <View style={[styles.iconBox, styles.iconBoxAction]}><Ionicons name="sparkles" size={17} color={colors.primary as string} /></View>
                <View style={styles.rowText}>
                  <Text style={[styles.rowTitle, { textAlign: rtl.textStart }]} numberOfLines={1}>{tr('askEmpty')}</Text>
                  <Text style={[styles.rowSubtitle, { textAlign: rtl.textStart }]} numberOfLines={1}>{tr('askHint')}</Text>
                </View>
                {desktop && <Text style={styles.kbd}>{/Mac/i.test(typeof navigator !== 'undefined' ? navigator.platform : '') ? '⌘J' : 'Ctrl J'}</Text>}
              </Pressable>
            );
          })()}
          {!!recents.opened.length && (
            <>
              {sectionHeader('opened', tr('recentlyOpened'), 'time-outline')}
              {recents.opened.map((h) => renderHit(h, true))}
            </>
          )}
          {!!recents.queries.length && (
            <>
              {sectionHeader('queries', tr('recentSearches'), 'search-outline', (
                <TouchableOpacity onPress={async () => { const next = { queries: [], opened: recents.opened }; setRecents(next); try { await AsyncStorage.setItem(recentKey(businessId), JSON.stringify(next)); } catch { /* best effort */ } }}>
                  <Text style={styles.link}>{tr('clear')}</Text>
                </TouchableOpacity>
              ))}
              {recents.queries.map((q) => {
                const i = ++index;
                return (
                  <Pressable key={`q-${q}`} {...rowProps(i)} onPress={() => setQuery(q)}>
                    <View style={styles.iconBox}><Ionicons name="time-outline" size={16} color={colors.text.secondary as string} /></View>
                    <Text style={[styles.rowTitle, styles.flex, { textAlign: rtl.textStart }]} numberOfLines={1}>{q}</Text>
                    <Ionicons name={rtl.isRTL ? 'arrow-back' : 'arrow-forward'} size={14} color={colors.text.disabled as string} />
                  </Pressable>
                );
              })}
            </>
          )}
          <View style={[styles.tips, !hasRecents && styles.tipsEmpty]}>
            {!hasRecents && (
              <>
                <View style={styles.heroIcon}><Ionicons name="sparkles-outline" size={26} color={colors.primary as string} /></View>
                <Text style={styles.heroTitle}>{tr('emptyTitle')}</Text>
                <Text style={styles.heroHint}>{tr('emptyHint')}</Text>
              </>
            )}
            <Text style={[styles.tipsTitle, { textAlign: hasRecents ? rtl.textStart : 'center' }]}>{tr('tryTitle')}</Text>
            <View style={[styles.tipChips, { flexDirection: rtl.rowDirection, justifyContent: hasRecents ? 'flex-start' : 'center' }]}>
              {(['orders', 'contacts', 'products', 'quotations', 'vendors', 'pages'] as SearchGroup[]).map((g) => (
                <TouchableOpacity key={g} style={[styles.tipChip, { flexDirection: rtl.rowDirection }]} onPress={() => { setQuery(`${PREFIX_OF[g]}: `); input.current?.focus(); }}>
                  <Ionicons name={GROUP_ICONS[g] as any} size={13} color={colors.text.secondary as string} />
                  <Text style={styles.tipChipText}>{PREFIX_OF[g]}:</Text>
                </TouchableOpacity>
              ))}
            </View>
            <Text style={[styles.heroHint, { textAlign: hasRecents ? rtl.textStart : 'center' }]}>{tr('tryHint')}</Text>
          </View>
        </>
      );
    }
    if (!result && loading) {
      return [0, 1, 2, 3].map((i) => (
        <View key={i} style={[styles.row, { flexDirection: rtl.rowDirection }]}>
          <View style={[styles.iconBox, styles.skeleton]} />
          <View style={styles.rowText}><View style={[styles.skeletonLine, { width: `${60 - i * 8}%` }]} /><View style={[styles.skeletonLine, styles.skeletonShort]} /></View>
        </View>
      ));
    }
    if (error && !result) {
      return (
        <View style={styles.empty}>
          <Ionicons name="cloud-offline-outline" size={28} color={colors.text.secondary as string} />
          <Text style={styles.heroTitle}>{tr('errorTitle')}</Text>
          <TouchableOpacity onPress={() => setRetry((n) => n + 1)}><Text style={styles.link}>{tr('retry')}</Text></TouchableOpacity>
        </View>
      );
    }
    const nothing = !groups.length;
    return (
      <>
        {nothing && !loading && (
          <View style={styles.empty}>
            <Ionicons name="search-outline" size={28} color={colors.text.secondary as string} />
            <Text style={styles.heroTitle}>{tr('noResults', { query: trimmed })}</Text>
            <Text style={styles.heroHint}>{group ? tr('noResultsInGroup', { group: tr(`group.${group}`) }) : tr('noResultsHint')}</Text>
            {!!group && <TouchableOpacity onPress={() => setGroup(null)}><Text style={styles.link}>{tr('searchEverywhere')}</Text></TouchableOpacity>}
          </View>
        )}
        {groups.map((g) => (
          <View key={g.key}>
            {sectionHeader(g.key, tr(`group.${g.key}`), GROUP_ICONS[g.key])}
            {g.hits.map((h) => renderHit(h))}
            {g.hasMore && g.seeAll && !group && (() => {
              const i = ++index;
              return (
                <Pressable key={`all-${g.key}`} {...rowProps(i)} onPress={() => g.seeAll && go(g.seeAll)}>
                  <View style={styles.iconBox}><Ionicons name="arrow-forward-circle-outline" size={18} color={colors.primary as string} /></View>
                  <Text style={[styles.link, styles.flex, { textAlign: rtl.textStart }]}>{tr('seeAll', { group: tr(`group.${g.key}`) })}</Text>
                </Pressable>
              );
            })()}
          </View>
        ))}
        {!!onAsk && (() => {
          const i = ++index;
          return (
            <View>
              {sectionHeader('ask', tr('assistant'), 'sparkles-outline')}
              <Pressable {...rowProps(i)} onPress={ask}>
                <View style={[styles.iconBox, styles.iconBoxAction]}><Ionicons name="sparkles" size={17} color={colors.primary as string} /></View>
                <View style={styles.rowText}>
                  <Text style={[styles.rowTitle, { textAlign: rtl.textStart }]} numberOfLines={1}>{tr('askAbout', { query: trimmed })}</Text>
                  <Text style={[styles.rowSubtitle, { textAlign: rtl.textStart }]} numberOfLines={1}>{tr('askHint')}</Text>
                </View>
              </Pressable>
            </View>
          );
        })()}
        {result?.partial && <Text style={styles.partial}>{tr('partial')}</Text>}
      </>
    );
  };

  const chips = trimmed && (chipGroups.length > 1 || group) ? (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.chipsScroll} contentContainerStyle={[styles.chips, { flexDirection: rtl.rowDirection }]} keyboardShouldPersistTaps="handled">
      {[null, ...chipGroups.map((g) => g.key)].map((key) => {
        const on = group === key;
        const count = key ? chipGroups.find((g) => g.key === key)?.hits.length : undefined;
        return (
          <TouchableOpacity key={key || 'all'} style={[styles.chip, on && styles.chipOn, { flexDirection: rtl.rowDirection }]} onPress={() => { setGroup(key); setSelected(0); input.current?.focus(); }}>
            {!!key && <Ionicons name={GROUP_ICONS[key] as any} size={12} color={(on ? colors.text.onPrimary : colors.text.secondary) as string} />}
            <Text style={[styles.chipText, on && styles.chipTextOn]}>{key ? tr(`group.${key}`) : tr('all')}</Text>
            {count !== undefined && <Text style={[styles.chipCount, on && styles.chipTextOn]}>{count}{chipGroups.find((g) => g.key === key)?.hasMore ? '+' : ''}</Text>}
          </TouchableOpacity>
        );
      })}
    </ScrollView>
  ) : null;

  const card = (
    <View style={[styles.card, desktop ? styles.cardDesktop : styles.cardMobile]} accessibilityViewIsModal>
      <View style={[styles.inputRow, { flexDirection: rtl.rowDirection }]}>
        <Ionicons name="search" size={20} color={colors.primary as string} />
        <TextInput
          ref={input}
          value={query}
          onChangeText={setQuery}
          placeholder={tr('placeholder')}
          placeholderTextColor={colors.text.disabled as string}
          style={[styles.input, { textAlign: rtl.textStart }, isWeb && ({ outlineStyle: 'none' } as any)]}
          autoCorrect={false}
          autoCapitalize="none"
          returnKeyType="search"
          onSubmitEditing={() => { if (!isWeb) activate(rows[0]); }}
          accessibilityLabel={tr('placeholder')}
        />
        {loading && !!result && <ActivityIndicator size="small" color={colors.primary as string} />}
        {!!query && (
          <TouchableOpacity accessibilityLabel={tr('clear')} onPress={() => { setQuery(''); input.current?.focus(); }} style={styles.clear}>
            <Ionicons name="close-circle" size={18} color={colors.text.disabled as string} />
          </TouchableOpacity>
        )}
        {desktop ? <Text style={styles.kbd}>Esc</Text> : (
          <TouchableOpacity onPress={onClose} style={styles.cancel}><Text style={styles.link}>{tr('cancel')}</Text></TouchableOpacity>
        )}
      </View>
      {loading && !!result && <View style={styles.progress} />}
      {chips}
      <ScrollView ref={scroll} style={styles.results} contentContainerStyle={styles.resultsContent} keyboardShouldPersistTaps="handled" keyboardDismissMode="on-drag">
        {body()}
      </ScrollView>
      {desktop && (
        <View style={[styles.footer, { flexDirection: rtl.rowDirection }]}>
          <Text style={styles.footerText}><Text style={styles.kbdSmall}>↑</Text> <Text style={styles.kbdSmall}>↓</Text> {tr('keys.move')}</Text>
          <Text style={styles.footerText}><Text style={styles.kbdSmall}>↵</Text> {tr('keys.open')}</Text>
          <Text style={styles.footerText}><Text style={styles.kbdSmall}>Tab</Text> {tr('keys.filter')}</Text>
          <Text style={styles.footerText}><Text style={styles.kbdSmall}>{/Mac/i.test(typeof navigator !== 'undefined' ? navigator.platform : '') ? '⌘' : 'Ctrl'}</Text> <Text style={styles.kbdSmall}>↵</Text> {tr('keys.edit')}</Text>
          <View style={styles.flex} />
          {!!result && !!trimmed && <Text style={styles.footerText}>{tr('took', { ms: result.tookMs })}</Text>}
        </View>
      )}
    </View>
  );

  return (
    // Phones: InsetModal keeps the sheet clear of the notch, status bar and home indicator.
    <InsetModal visible={visible} transparent={desktop} animationType={desktop ? 'fade' : 'slide'} onRequestClose={onClose}>
      {desktop ? (
        <Pressable style={styles.backdrop} onPress={onClose} accessibilityLabel={tr('close')}>
          <Pressable onPress={(e) => e.stopPropagation?.()} style={styles.cardWrap}>{card}</Pressable>
        </Pressable>
      ) : <View style={styles.mobileRoot}>{card}</View>}
    </InsetModal>
  );
};

const getStyles = () => StyleSheet.create({
  flex: { flex: 1 },
  backdrop: { flex: 1, alignItems: 'center', paddingTop: '9vh' as any, paddingHorizontal: spacing.md, ...overlayBackdropStyle() },
  cardWrap: { width: '100%', maxWidth: 720, cursor: 'auto' as any },
  mobileRoot: { flex: 1, backgroundColor: colors.background.surfaceRaised },
  card: { backgroundColor: colors.background.surfaceRaised, overflow: 'hidden' },
  cardDesktop: { ...overlayCardStyle(), maxHeight: '78vh' as any },
  cardMobile: { flex: 1 },
  inputRow: { flexShrink: 0, alignItems: 'center', gap: spacing.sm, paddingHorizontal: spacing.md, paddingVertical: spacing.sm + 2, borderBottomWidth: 1, borderBottomColor: colors.border },
  input: { flex: 1, fontSize: typography.fontSize.lg, color: colors.text.primary, paddingVertical: spacing.sm },
  clear: { padding: 4 },
  cancel: { paddingHorizontal: spacing.xs },
  kbd: { fontSize: 11, color: colors.text.secondary, borderWidth: 1, borderColor: colors.border, borderRadius: 6, paddingHorizontal: 6, paddingVertical: 2 },
  kbdSmall: { fontSize: 10, color: colors.text.secondary, borderWidth: 1, borderColor: colors.border, borderRadius: 4, paddingHorizontal: 4 },
  progress: { height: 2, backgroundColor: colors.primary, opacity: 0.6 },
  // Never squeezed by the results list below it (it was cut short once results filled the sheet).
  chipsScroll: { flexGrow: 0, flexShrink: 0, height: 50, minHeight: 50, maxHeight: 50, borderBottomWidth: 1, borderBottomColor: colors.border },
  chips: { alignItems: 'center', gap: 6, height: 50, paddingHorizontal: spacing.md },
  chip: { alignItems: 'center', gap: 5, minHeight: 32, paddingHorizontal: 12, paddingVertical: 6, borderRadius: 999, borderWidth: 1, borderColor: colors.border },
  chipOn: { backgroundColor: colors.primary, borderColor: colors.primary },
  chipText: { fontSize: typography.fontSize.xs, fontWeight: '600', color: colors.text.secondary },
  chipTextOn: { color: colors.text.onPrimary },
  chipCount: { fontSize: 10, color: colors.text.disabled, fontWeight: '700' },
  results: { flexGrow: 0, flexShrink: 1 },
  resultsContent: { paddingVertical: spacing.xs, paddingBottom: spacing.md },
  sectionHeader: { alignItems: 'center', gap: 6, paddingHorizontal: spacing.md, paddingTop: spacing.md, paddingBottom: 4 },
  sectionTitle: { fontSize: 11, fontWeight: '700', letterSpacing: 0.6, textTransform: 'uppercase', color: colors.text.secondary },
  row: { alignItems: 'center', gap: spacing.sm, marginHorizontal: spacing.xs, paddingHorizontal: spacing.sm, paddingVertical: 8, borderRadius: 10, minHeight: 48 },
  rowActive: { backgroundColor: colors.primaryTransparent },
  iconBox: { width: 34, height: 34, borderRadius: 9, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.background.secondary },
  iconBoxAction: { backgroundColor: colors.primaryTransparent },
  thumb: { width: 34, height: 34, borderRadius: 9, backgroundColor: colors.background.secondary },
  rowText: { flex: 1, minWidth: 0, gap: 2 },
  titleLine: { alignItems: 'center', gap: 6 },
  rowTitle: { flexShrink: 1, fontSize: typography.fontSize.sm, fontWeight: '600', color: colors.text.primary },
  rowSubtitle: { fontSize: typography.fontSize.xs, color: colors.text.secondary },
  mark: { color: colors.primary, fontWeight: '800' },
  badge: { paddingHorizontal: 7, paddingVertical: 1, borderRadius: 999 },
  badgeText: { fontSize: 10, fontWeight: '700' },
  metaLine: { gap: 4, marginTop: 2, flexWrap: 'wrap' },
  metaChip: { fontSize: 10, color: colors.text.secondary, backgroundColor: colors.background.secondary, borderRadius: 4, paddingHorizontal: 5, paddingVertical: 1, maxWidth: 140 },
  rowEnd: { gap: 2, marginStart: spacing.xs },
  amount: { fontSize: typography.fontSize.sm, fontWeight: '700', color: colors.text.primary },
  date: { fontSize: 11, color: colors.text.secondary },
  rowActions: { gap: 4 },
  miniButton: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 7, paddingVertical: 5, borderRadius: 7, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background.surface },
  miniText: { fontSize: 11, fontWeight: '600', color: colors.text.secondary },
  link: { color: colors.primary, fontWeight: '700', fontSize: typography.fontSize.sm },
  empty: { alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.xl, paddingHorizontal: spacing.lg },
  tips: { gap: spacing.sm, paddingHorizontal: spacing.md, paddingTop: spacing.md },
  tipsEmpty: { alignItems: 'center', paddingVertical: spacing.xl },
  heroIcon: { width: 52, height: 52, borderRadius: 16, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.primaryTransparent },
  heroTitle: { fontSize: typography.fontSize.base, fontWeight: '700', color: colors.text.primary, textAlign: 'center' },
  heroHint: { fontSize: typography.fontSize.xs, lineHeight: 18, color: colors.text.secondary, textAlign: 'center', maxWidth: 460 },
  tipsTitle: { fontSize: 11, fontWeight: '700', letterSpacing: 0.6, textTransform: 'uppercase', color: colors.text.secondary, marginTop: spacing.sm },
  tipChips: { flexWrap: 'wrap', gap: 6 },
  tipChip: { alignItems: 'center', gap: 5, paddingHorizontal: 10, paddingVertical: 6, borderRadius: 8, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background.surface },
  tipChipText: { fontSize: typography.fontSize.xs, fontWeight: '600', color: colors.text.primary, fontFamily: isWeb ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : undefined },
  skeleton: { backgroundColor: colors.border },
  skeletonLine: { height: 10, borderRadius: 5, backgroundColor: colors.border, marginVertical: 3 },
  skeletonShort: { width: '35%' },
  partial: { fontSize: 11, color: colors.text.secondary, textAlign: 'center', paddingTop: spacing.sm },
  footer: { alignItems: 'center', gap: spacing.md, paddingHorizontal: spacing.md, paddingVertical: 8, borderTopWidth: 1, borderTopColor: colors.border, backgroundColor: colors.background.surface },
  footerText: { fontSize: 11, color: colors.text.secondary },
});
